import { Fragment, useEffect, useState } from "react";
import DataTable from "react-data-table-component";
import moment from "moment";
import axios from "axios";
import { toast } from "react-toastify";
import { useNavigate } from "react-router";
import {
  Card,
  CardBody,
  Col,
  Container,
  Row,
  Button,
  Badge,
  DropdownMenu,
  DropdownItem,
  DropdownToggle,
  UncontrolledDropdown,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
} from "reactstrap";

import {
  MoreVertical,
  PlusCircle,
  Eye,
  Monitor,
  Smartphone,
} from "react-feather";

import Loader from "../../../Loader/Loader";
import useRbac from "../../../../Hooks/useRbac";
import { baseURL } from "../../../../Services/api/baseURL";

// Matches flashDealController's action-based routes, mounted at /api/flash-deals.
const FLASH_DEAL_API = `${baseURL}/api/flash-deals`;

const FlashDealTable = () => {
  const navigate = useNavigate();
  const { can } = useRbac();

  const [loading, setLoading] = useState(false);
  const [deals, setDeals] = useState([]);
  const [search, setSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalRows, setTotalRows] = useState(0);
  const [sortBy, setSortBy] = useState("createdAt");
  const [sortOrder, setSortOrder] = useState("desc");
  const [statusFilter, setStatusFilter] = useState("all");
  const [bannerModal, setBannerModal] = useState(false);
  const [selectedBanner, setSelectedBanner] = useState(null);
  const [activeTab, setActiveTab] = useState("website");

  // ====================================
  // Navigation
  // ====================================

  const handleAddFlashDeal = () => {
    navigate("/flash-deals/create");
  };

  const handleEditFlashDeal = (id) => {
    navigate(`/flash-deals/edit/${id}`);
  };

  const handleViewFlashDeal = (id) => {
    navigate(`/flash-deals/view/${id}`);
  };

  // ====================================
  // Banner Preview Modal
  // ====================================

  const openBannerPreview = (deal) => {
    setSelectedBanner({
      website: deal?.websiteBanner || null,
      mobile: deal?.mobileBanner || null,
      name: deal?.title,
    });
    setActiveTab("website");
    setBannerModal(true);
  };

  const closeBannerPreview = () => {
    setBannerModal(false);
    setSelectedBanner(null);
  };

  // ====================================
  // Live / Countdown Status
  // ====================================

  const getLiveStatus = (deal) => {
    const now = new Date();
    const start = new Date(deal.startDate);
    const end = new Date(deal.endDate);

    if (now < start) {
      return { text: "Upcoming", color: "warning" };
    }
    if (now > end) {
      return { text: "Expired", color: "dark" };
    }
    return { text: "Running", color: "success" };
  };

  // ====================================
  // Fetch Flash Deals
  // ====================================

  const fetchFlashDeals = async () => {
    setLoading(true);
    try {
      const token = JSON.parse(localStorage.getItem("token"));

      const params = {
        page: currentPage,
        limit: rowsPerPage,
        search,
        status: statusFilter,
        sortBy,
        sortOrder,
      };

      Object.keys(params).forEach((key) => {
        if (params[key] === "" || params[key] === "all") {
          delete params[key];
        }
      });

      const response = await axios.get(`${FLASH_DEAL_API}/get-flash-deals`, {
        headers: { Authorization: token },
        params,
      });

      if (response.data.success) {
        setDeals(response.data.data || []);
        setTotalRows(response.data.pagination?.totalCount || 0);
      } else {
        toast.error("Unable to fetch flash deals");
        setDeals([]);
        setTotalRows(0);
      }
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.message || "Failed to fetch flash deals");
      setDeals([]);
      setTotalRows(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const delay = setTimeout(
      () => {
        fetchFlashDeals();
      },
      search ? 500 : 0
    );

    return () => clearTimeout(delay);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, rowsPerPage, search, statusFilter, sortBy, sortOrder]);

  // ====================================
  // Delete Flash Deal
  // ====================================

  const deleteDeal = async (id) => {
    if (!window.confirm("Are you sure you want to delete this flash deal?")) return;

    setLoading(true);
    try {
      const token = JSON.parse(localStorage.getItem("token"));

      const response = await axios.delete(
        `${FLASH_DEAL_API}/delete-flash-deal/${id}`,
        { headers: { Authorization: token } }
      );

      if (response.data.success) {
        toast.success(response.data.message || "Flash deal deleted successfully!");
        fetchFlashDeals();
      } else {
        toast.error(response.data.message || "Unable to delete flash deal");
      }
    } catch (err) {
      toast.error(err.response?.data?.message || "Unable to delete flash deal");
    } finally {
      setLoading(false);
    }
  };

  // ====================================
  // Search / Filter / Table Events
  // ====================================

  const handleSearch = (e) => {
    setSearch(e.target.value);
    setCurrentPage(1);
  };

  const handleStatus = (status) => {
    setStatusFilter(status);
    setCurrentPage(1);
  };

  const handleSort = (column, direction) => {
    setSortBy(column.sortField || "createdAt");
    setSortOrder(direction);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleRowsChange = (rows) => {
    setRowsPerPage(rows);
    setCurrentPage(1);
  };

  // ====================================
  // Table Columns
  // ====================================

  const columns = [
    {
      name: "Deal Title",
      width: "180px",
      selector: (row) => row?.title || "N/A",
      sortable: true,
      sortField: "title",
      cell: (row) => (
        <span
          style={{ fontWeight: 500, color: "#007bff", cursor: "pointer" }}
          onClick={() => handleViewFlashDeal(row._id)}
          title="View flash deal details"
        >
          {row?.title || "N/A"}
        </span>
      ),
    },
    {
      name: "Banners",
      width: "140px",
      cell: (row) => {
        const hasWebsite = row?.websiteBanner;
        const hasMobile = row?.mobileBanner;

        if (!hasWebsite && !hasMobile) {
          return (
            <span className="text-muted" style={{ fontSize: "11px" }}>
              No Banners
            </span>
          );
        }

        return (
          <div className="d-flex align-items-center justify-content-center flex-column">
            {hasWebsite && (
              <div className="mb-1 d-flex align-items-center">
                <Monitor size={12} className="me-1" />
                <img
                  src={hasWebsite}
                  alt="Website Banner"
                  style={{
                    width: "60px",
                    height: "6px",
                    objectFit: "cover",
                    border: "1px solid #007bff",
                    borderRadius: "2px",
                    cursor: "pointer",
                  }}
                  onClick={() => openBannerPreview(row)}
                  title="Click to view banners"
                />
              </div>
            )}
            {hasMobile && (
              <div className="mb-1 d-flex align-items-center">
                <Smartphone size={12} className="me-1" />
                <img
                  src={hasMobile}
                  alt="Mobile Banner"
                  style={{
                    width: "40px",
                    height: "22px",
                    objectFit: "cover",
                    border: "1px solid #28a745",
                    borderRadius: "2px",
                    cursor: "pointer",
                  }}
                  onClick={() => openBannerPreview(row)}
                  title="Click to view banners"
                />
              </div>
            )}
            <Button
              size="sm"
              color="link"
              className="p-0 mt-1"
              onClick={() => openBannerPreview(row)}
              title="View banners"
            >
              <Eye size={12} />
            </Button>
          </div>
        );
      },
    },
    {
      name: "Countdown Window",
      width: "180px",
      selector: (row) => row?.startDate,
      sortable: true,
      sortField: "startDate",
      cell: (row) => {
        const live = getLiveStatus(row);
        return (
          <div>
            <div style={{ fontSize: "12px" }}>
              {row?.startDate ? moment(row.startDate).format("DD MMM, h:mm A") : "N/A"}
              {" - "}
              {row?.endDate ? moment(row.endDate).format("DD MMM, h:mm A") : "N/A"}
            </div>
            <Badge color={live.color} pill className="mt-1" style={{ fontSize: "10px" }}>
              {live.text}
            </Badge>
          </div>
        );
      },
    },
    {
      name: "Product",
      cell: (row) => {
        const productName = row?.product?.productName || "Unknown";
        const variantName = row?.variant?.variantName;
        const label = variantName ? `${productName} - ${variantName}` : productName;

        return row?.product ? (
          <Badge color="info" pill style={{ fontSize: "10px" }} title={label}>
            {label}
          </Badge>
        ) : (
          <span className="text-muted">No Product</span>
        );
      },
    },
    {
      name: "Price",
      width: "130px",
      cell: (row) => (
        <div style={{ fontSize: "12px" }}>
          <span style={{ textDecoration: "line-through", opacity: 0.5 }}>
            ${Number(row?.originalPrice ?? 0).toFixed(2)}
          </span>{" "}
          <span style={{ fontWeight: 600, color: "#d3178a" }}>
            ${Number(row?.dealPrice ?? 0).toFixed(2)}
          </span>
        </div>
      ),
    },
    {
      name: "Status",
      width: "100px",
      selector: (row) => row?.status,
      sortable: true,
      sortField: "status",
      cell: (row) => (
        <Badge color={row.status === "active" ? "success" : "secondary"} pill className="px-2">
          {row.status}
        </Badge>
      ),
    },
    {
      name: "Created",
      width: "120px",
      selector: (row) => row?.createdAt,
      sortable: true,
      sortField: "createdAt",
      cell: (row) => (
        <span>{row?.createdAt ? moment(row.createdAt).format("DD MMM YYYY") : "N/A"}</span>
      ),
    },
    {
      name: "Actions",
      width: "100px",
      omit: !can("items.update") && !can("items.delete"),
      center: true,
      ignoreRowClick: true,
      allowOverflow: true,
      button: true,
      cell: (row) => (
        <UncontrolledDropdown className="action_dropdown mx-2">
          <DropdownToggle className="action_btn">
            <MoreVertical color="#000" size={16} />
          </DropdownToggle>
          <DropdownMenu>
            <DropdownItem onClick={() => handleViewFlashDeal(row._id)}>View Deal</DropdownItem>
            {can("items.update") && (
              <DropdownItem onClick={() => handleEditFlashDeal(row._id)}>Edit Deal</DropdownItem>
            )}
            {(row?.websiteBanner || row?.mobileBanner) && (
              <DropdownItem onClick={() => openBannerPreview(row)}>
                View Banners
              </DropdownItem>
            )}
            {can("items.delete") && (
              <DropdownItem onClick={() => deleteDeal(row._id)}>Delete</DropdownItem>
            )}
          </DropdownMenu>
        </UncontrolledDropdown>
      ),
    },
  ];

  return (
    <Fragment>
      <Container fluid style={{ paddingTop: 30 }}>
        <Row>
          <Col sm="12">
            <Card>
              <CardBody>
                {/* Header */}
                <Row className="align-items-center mb-3">
                  <Col md={4}>
                    <h4 className="mb-0">Flash Deals</h4>
                  </Col>

                  <Col md={8}>
                    <div className="d-flex justify-content-end align-items-center flex-wrap">
                      {/* Status */}
                      <div className="me-3">
                        <Button
                          size="sm"
                          color={statusFilter === "all" ? "primary" : "outline-primary"}
                          className="me-1"
                          onClick={() => handleStatus("all")}
                        >
                          All
                        </Button>
                        <Button
                          size="sm"
                          color={statusFilter === "active" ? "success" : "outline-success"}
                          className="me-1"
                          onClick={() => handleStatus("active")}
                        >
                          Active
                        </Button>
                        <Button
                          size="sm"
                          color={statusFilter === "inactive" ? "secondary" : "outline-secondary"}
                          onClick={() => handleStatus("inactive")}
                        >
                          Inactive
                        </Button>
                      </div>

                      {/* Search */}
                      <div className="position-relative me-3">
                        <i
                          className="fa fa-search"
                          style={{ position: "absolute", left: 10, top: 11, color: "#888" }}
                        />
                        <input
                          className="form-control ps-5"
                          placeholder="Search Flash Deal..."
                          value={search}
                          onChange={handleSearch}
                        />
                      </div>

                      {/* Add */}
                      {can("items.create") && (
                        <Button color="primary" onClick={handleAddFlashDeal}>
                          <PlusCircle size={15} className="me-1" />
                          Add Flash Deal
                        </Button>
                      )}
                    </div>
                  </Col>
                </Row>

                {/* Table */}
                <DataTable
                  data={deals}
                  columns={columns}
                  striped
                  highlightOnHover
                  responsive
                  pagination
                  paginationServer
                  paginationTotalRows={totalRows}
                  paginationPerPage={rowsPerPage}
                  paginationDefaultPage={currentPage}
                  onChangePage={handlePageChange}
                  onChangeRowsPerPage={handleRowsChange}
                  onSort={handleSort}
                  sortServer
                  progressPending={loading}
                  progressComponent={<Loader />}
                  noDataComponent={<div className="py-5 text-center">No Flash Deals Found</div>}
                />
              </CardBody>
            </Card>
          </Col>
        </Row>

        {/* Banner Preview */}
        <Modal isOpen={bannerModal} toggle={closeBannerPreview} centered size="xl">
          <ModalHeader toggle={closeBannerPreview}>{selectedBanner?.name}</ModalHeader>

          <ModalBody>
            <Nav tabs>
              <NavItem>
                <NavLink
                  className={activeTab === "website" ? "active" : ""}
                  style={{ cursor: "pointer" }}
                  onClick={() => setActiveTab("website")}
                >
                  <Monitor size={15} className="me-1" />
                  Website Banner
                </NavLink>
              </NavItem>
              <NavItem>
                <NavLink
                  className={activeTab === "mobile" ? "active" : ""}
                  style={{ cursor: "pointer" }}
                  onClick={() => setActiveTab("mobile")}
                >
                  <Smartphone size={15} className="me-1" />
                  Mobile Banner
                </NavLink>
              </NavItem>
            </Nav>

            <TabContent activeTab={activeTab} className="mt-4">
              <TabPane tabId="website">
                {selectedBanner?.website ? (
                  <div className="text-center">
                    <img
                      src={selectedBanner.website}
                      alt=""
                      style={{ width: "100%", borderRadius: 8, border: "1px solid #ddd" }}
                    />
                    <small className="text-muted d-block mt-2">Website Banner (11 : 1)</small>
                  </div>
                ) : (
                  <div className="text-center py-5">No Website Banner</div>
                )}
              </TabPane>

              <TabPane tabId="mobile">
                {selectedBanner?.mobile ? (
                  <div className="text-center">
                    <img
                      src={selectedBanner.mobile}
                      alt=""
                      style={{ width: "60%", borderRadius: 8, border: "1px solid #ddd" }}
                    />
                    <small className="text-muted d-block mt-2">Mobile Banner (16 : 9)</small>
                  </div>
                ) : (
                  <div className="text-center py-5">No Mobile Banner</div>
                )}
              </TabPane>
            </TabContent>
          </ModalBody>

          <ModalFooter>
            <Button color="secondary" onClick={closeBannerPreview}>
              Close
            </Button>
            {activeTab === "website" && selectedBanner?.website && (
              <Button color="primary" onClick={() => window.open(selectedBanner.website, "_blank")}>
                Open Website Banner
              </Button>
            )}
            {activeTab === "mobile" && selectedBanner?.mobile && (
              <Button color="success" onClick={() => window.open(selectedBanner.mobile, "_blank")}>
                Open Mobile Banner
              </Button>
            )}
          </ModalFooter>
        </Modal>
      </Container>
    </Fragment>
  );
};

export default FlashDealTable;